import { CHASSIS_BEATS, zombieById } from "@zend/content";
import { combatantFromZombie, combatantMeta, resolveAbilities } from "./snapshot.js";
import { applyStatus, effectiveAtk, effectiveCrit, effectiveDef, energyGain, other, status, statusValue, type BattleState, type Unit } from "./state.js";
import { allies, deadFighter, enemies, pickEnemy, strongestFighter, weakestAlly } from "./targeting.js";
import { CHASSIS_BONUS, ENERGY_MAX, ENERGY_ON_HIT, type ResolvedAbility } from "./types.js";
import { hitChance } from "./tactics.js";

export interface DamageOptions {
  /** Ignore this share of the target's defence (0..1). */
  pierce?: number;
  /** Skip the hit roll (DoTs, explosions). */
  sure?: boolean;
  /** Marks the damage as a pulse/ability hit for events. */
  label?: string;
  /** No crit roll (DoTs, traps). */
  noCrit?: boolean;
  /** The source may already be wrecked (death explosions). */
  fromDead?: boolean;
}

export function heal(state: BattleState, source: Unit | null, target: Unit, amount: number): number {
  if (!target.alive) return 0;
  const healed = Math.min(Math.round(amount), target.maxHp - target.hp);
  if (healed <= 0) return 0;
  target.hp += healed;
  if (source) source.healing += healed;
  state.events.push({ t: state.tick, k: "heal", a: source?.input.id ?? target.input.id, b: target.input.id, v: healed });
  return healed;
}

export function giveShield(state: BattleState, source: Unit, target: Unit, amount: number): void {
  target.shield = Math.max(target.shield, Math.round(amount));
  state.events.push({ t: state.tick, k: "shield", a: source.input.id, b: target.input.id, v: Math.round(amount) });
}

function onDeath(state: BattleState, victim: Unit, killer: Unit | null): void {
  victim.alive = false;
  victim.hp = 0;
  victim.statuses = [];
  if (killer) killer.kills++;
  state.events.push({ t: state.tick, k: "death", a: killer?.input.id ?? "", b: victim.input.id });
  // explode_on_death: hits every enemy of the victim.
  const explode = victim.abilities.find((a) => a.archetype === "abl.explode_on_death");
  if (explode) {
    for (const e of enemies(state, victim)) dealDamage(state, victim, e, (explode.params["mult"] ?? 1.5) * explode.magnitude, { sure: true, noCrit: true, label: "explode", fromDead: true });
  }
  // last_stand of the victim's commander: repair every other ally when a fighter falls.
  if (victim.kind === "fighter") {
    const commander = state.sides[victim.side].units.find((u) => u.kind === "commander" && u.alive);
    const ls = commander?.abilities.find((a) => a.archetype === "abl.last_stand");
    if (commander && ls) {
      for (const ally of allies(state, commander)) heal(state, commander, ally, ally.maxHp * ((ls.params["pct"] ?? 20) / 100) * ls.magnitude);
    }
  }
}

/** Damage pipeline of R30: DEF mitigation, chassis triangle, crit, variance, marks, shields, bulwark, reflect. */
export function dealDamage(state: BattleState, from: Unit, to: Unit, mult: number, opts: DamageOptions = {}): number {
  if (!to.alive || (!from.alive && !opts.fromDead)) return 0;
  const rng = state.rng;
  const chance = opts.sure ? 1 : hitChance(from.stats.acc * (1 + statusValue(from, "acc_pct") / 100), to.stats.eva, state.rulesVersion ?? 1);
  if (!opts.sure) {
    if (!rng.chance(chance)) {
      state.events.push({ t: state.tick, k: "miss", a: from.input.id, b: to.input.id });
      return 0;
    }
  }
  const def = effectiveDef(state, to) * (1 - (opts.pierce ?? 0));
  const mitigation = 1 - def / (def + 600 + 20 * from.input.level);
  let dmg = effectiveAtk(state, from) * mult * mitigation;
  const chassis = from.meta.chassis && to.meta.chassis && CHASSIS_BEATS[from.meta.chassis] === to.meta.chassis ? CHASSIS_BONUS : 1;
  dmg *= chassis;
  let crit = false;
  if (!opts.noCrit && rng.chance(effectiveCrit(state, from))) {
    dmg *= from.stats.critDmg;
    crit = true;
  }
  dmg *= 0.95 + rng.next() * 0.1;
  const marked = status(to, "marked");
  if (marked) dmg *= 1 + marked.value / 100;
  if (from.protocolBonusPct > 0 && from.kind !== "support") dmg *= 1 + from.protocolBonusPct / 100;
  const revengePct = state.sides[from.side].modifiers.revengePct;
  if (revengePct > 0) dmg *= 1 + revengePct / 100;
  const bulwark = to.abilities.find((a) => a.archetype === "abl.bulwark");
  if (bulwark) dmg = Math.min(dmg, to.maxHp * ((bulwark.params["cap"] ?? 20) / 100));
  dmg = Math.max(1, Math.round(dmg));
  // The Bastion Wall absorbs damage aimed at the defending side while it stands (R17).
  const side = state.sides[to.side];
  if (side.wallHp > 0 && from.side !== to.side && to.kind !== "turret") {
    const absorbed = Math.min(side.wallHp, dmg);
    side.wallHp -= absorbed;
    side.wallAbsorbed += absorbed;
    dmg -= absorbed;
    state.events.push({ t: state.tick, k: "wall", a: from.input.id, b: to.input.id, v: absorbed });
    if (dmg <= 0) return absorbed;
  }
  let shieldAbsorbed = 0;
  if (to.shield > 0) {
    const absorbed = Math.min(to.shield, dmg);
    shieldAbsorbed = absorbed;
    to.shield -= absorbed;
    dmg -= absorbed;
  }
  to.hp -= dmg;
  from.damageDealt += dmg;
  to.damageTaken += dmg;
  if (from.kind === "turret") state.turretDamage += dmg;
  state.events.push({ t: state.tick, k: "damage", a: from.input.id, b: to.input.id, v: dmg, x: `${opts.label ?? "hit"}${crit ? "!" : ""}`, ...(state.rulesVersion === 2 ? { detail: { hitChance: chance, attack: Math.round(effectiveAtk(state, from)), defence: Math.round(def), mitigation, chassis, critical: crit, shieldAbsorbed, moralePct: from.input.kind === "unit" ? state.sides[from.side].morale?.atkPct ?? 0 : 0, ...(revengePct > 0 ? { revengePct } : {}) } } : {}) });
  if (to.alive && to.kind !== "turret") to.energy = Math.min(ENERGY_MAX, to.energy + energyGain(state, to, ENERGY_ON_HIT));
  const reflect = to.abilities.find((a) => a.archetype === "abl.reflect");
  if (reflect && from.alive && !opts.fromDead && opts.label !== "reflect") {
    const back = Math.round(dmg * ((reflect.params["pct"] ?? 15) / 100) * reflect.magnitude);
    if (back > 0) {
      from.hp -= back;
      from.damageTaken += back;
      to.damageDealt += back;
      state.events.push({ t: state.tick, k: "damage", a: to.input.id, b: from.input.id, v: back, x: "reflect" });
      if (from.hp <= 0) onDeath(state, from, to);
    }
  }
  if (to.hp <= 0) onDeath(state, to, from);
  return dmg;
}

function multiHit(state: BattleState, actor: Unit, target: Unit, hits: number, mult: number, label: string): void {
  for (let i = 0; i < hits && target.alive && actor.alive; i++) dealDamage(state, actor, target, mult, { label });
}

/**
 * Executes one ability (standard, active, ultimate or pulse) for `actor`. Every archetype of the content
 * library is handled here exactly once (30-units.md R23).
 */
export function useAbility(state: BattleState, actor: Unit, ab: ResolvedAbility): void {
  const p = ab.params;
  const m = ab.magnitude;
  const label = ab.archetype.slice(4);
  const enemy = () => pickEnemy(state, actor);
  state.events.push({ t: state.tick, k: ab.kind === "standard" ? "attack" : "ability", a: actor.input.id, x: label });
  if (ab.kind !== "standard") actor.abilitiesUsed++;
  switch (ab.archetype) {
    case "abl.single_strike": {
      const t = enemy();
      if (t) dealDamage(state, actor, t, (p["mult"] ?? 1) * m, { label });
      return;
    }
    case "abl.command_pulse": {
      const t = enemy();
      if (t) dealDamage(state, actor, t, (p["mult"] ?? 0.8) * m, { label });
      return;
    }
    case "abl.multi_strike": {
      const targets = enemies(state, actor).slice(0, Math.round(p["targets"] ?? 2));
      for (const t of targets) dealDamage(state, actor, t, (p["mult"] ?? 0.7) * m, { label });
      return;
    }
    case "abl.aoe_blast":
    case "abl.ultimate_orbital": {
      for (const t of enemies(state, actor)) dealDamage(state, actor, t, (p["mult"] ?? 0.6) * m, { label });
      return;
    }
    case "abl.pierce": {
      const t = enemy();
      if (t) dealDamage(state, actor, t, (p["mult"] ?? 1.2) * m, { pierce: Math.min(0.9, ((p["pct"] ?? 30) / 100) * m), label });
      return;
    }
    case "abl.execute": {
      const t = pickEnemy(state, actor, "lowest_hp_pct");
      if (!t) return;
      const below = t.hp / t.maxHp < (p["threshold"] ?? 30) / 100;
      dealDamage(state, actor, t, 1 * (below ? 1 + ((p["bonus"] ?? 100) / 100) * m : 1), { label });
      return;
    }
    case "abl.chain": {
      const pool = enemies(state, actor);
      let mult = (p["mult"] ?? 1) * m;
      for (let i = 0; i < Math.min(pool.length, Math.round(p["bounces"] ?? 2)); i++) {
        dealDamage(state, actor, pool[i]!, mult, { label });
        mult *= (p["decay"] ?? 70) / 100;
      }
      return;
    }
    case "abl.snipe_rear": {
      const t = pickEnemy(state, actor, "rear");
      if (t) dealDamage(state, actor, t, (p["mult"] ?? 1.6) * m, { label });
      return;
    }
    case "abl.barrage": {
      const t = enemy();
      if (t) multiHit(state, actor, t, Math.round(p["hits"] ?? 3), (p["mult"] ?? 0.4) * m, label);
      return;
    }
    case "abl.overload": {
      const t = enemy();
      if (t) dealDamage(state, actor, t, (p["mult"] ?? 2.5) * m, { label });
      const self = Math.round(actor.maxHp * ((p["selfPct"] ?? 10) / 100));
      actor.hp = Math.max(1, actor.hp - self);
      actor.damageTaken += self;
      state.events.push({ t: state.tick, k: "damage", a: actor.input.id, b: actor.input.id, v: self, x: "overload_self" });
      return;
    }
    case "abl.explode_on_death":
    case "abl.reflect":
    case "abl.bulwark":
    case "abl.last_stand":
    case "abl.protocol":
    case "abl.aura":
      return; // passives are evaluated where they matter
    case "abl.summon": {
      const side = state.sides[actor.side];
      const count = Math.round(p["count"] ?? 2);
      for (let i = 0; i < count && side.summoned < 6; i++) {
        side.summoned++;
        const id = `${actor.input.id}_x${side.summoned}`;
        const input = combatantFromZombie(id, zombieById("zmb.shambler"), actor.input.level, "extra");
        const meta = combatantMeta(input);
        const unit: Unit = {
          input,
          meta,
          side: actor.side,
          slot: "extra",
          kind: "fighter",
          stats: input.stats,
          maxHp: input.stats.hp,
          hp: input.stats.hp,
          energy: 0,
          shield: 0,
          alive: true,
          statuses: [],
          nextAttackTick: state.tick + input.stats.intervalTicks,
          abilities: resolveAbilities(input),
          protocol: null,
          protocolBonusPct: 0,
          damageDealt: 0,
          damageTaken: 0,
          healing: 0,
          kills: 0,
          abilitiesUsed: 0,
        };
        side.units.push(unit);
        state.events.push({ t: state.tick, k: "summon", a: actor.input.id, b: id, x: "zmb.shambler" });
      }
      return;
    }
    case "abl.armor_break": {
      const t = enemy();
      if (t) applyStatus(state, t, "def_pct", -(p["pct"] ?? 25) * m, p["ticks"] ?? 30, actor.input.id);
      return;
    }
    case "abl.emp": {
      const t = enemy();
      if (t) {
        t.energy = Math.max(0, t.energy - Math.round((p["drain"] ?? 40) * m));
        state.events.push({ t: state.tick, k: "status", a: actor.input.id, b: t.input.id, v: Math.round((p["drain"] ?? 40) * m), x: "emp" });
      }
      return;
    }
    case "abl.suppress": {
      const t = enemy();
      if (t) applyStatus(state, t, "speed_pct", -Math.min(80, (p["pct"] ?? 25) * m), p["ticks"] ?? 30, actor.input.id);
      return;
    }
    case "abl.mark":
    case "abl.pulse_focus": {
      const t = enemy();
      if (t) applyStatus(state, t, "marked", (p["pct"] ?? 20) * m, p["ticks"] ?? 40, actor.input.id);
      return;
    }
    case "abl.blind": {
      const t = enemy();
      if (t) applyStatus(state, t, "acc_pct", -(p["pct"] ?? 25) * m, p["ticks"] ?? 30, actor.input.id);
      return;
    }
    case "abl.corrode": {
      const t = enemy();
      if (t) applyStatus(state, t, "corrode", (p["pctPerSec"] ?? 3) * m, p["ticks"] ?? 50, actor.input.id);
      return;
    }
    case "abl.shock": {
      const t = enemy();
      if (t) {
        dealDamage(state, actor, t, (p["mult"] ?? 1) * m, { label });
        if (t.alive) applyStatus(state, t, "stun", 1, p["ticks"] ?? 8, actor.input.id);
      }
      return;
    }
    case "abl.ultimate_lockdown": {
      for (const t of enemies(state, actor)) applyStatus(state, t, "stun", 1, (p["ticks"] ?? 15) * m, actor.input.id);
      return;
    }
    case "abl.shield_self": {
      giveShield(state, actor, actor, actor.maxHp * ((p["pct"] ?? 25) / 100) * m);
      applyStatus(state, actor, "fortify", 0, p["ticks"] ?? 50, actor.input.id);
      return;
    }
    case "abl.shield_ally": {
      const t = weakestAlly(state, actor);
      if (t) giveShield(state, actor, t, t.maxHp * ((p["pct"] ?? 20) / 100) * m);
      return;
    }
    case "abl.taunt": {
      applyStatus(state, actor, "taunt", 1, (p["ticks"] ?? 30) * m, actor.input.id);
      return;
    }
    case "abl.fortify": {
      applyStatus(state, actor, "fortify", (p["pct"] ?? 25) * m, p["ticks"] ?? 50, actor.input.id);
      return;
    }
    case "abl.pulse_fortify": {
      for (const a of allies(state, actor)) applyStatus(state, a, "def_pct", (p["pct"] ?? 15) * m, p["ticks"] ?? 30, actor.input.id);
      return;
    }
    case "abl.repair_pulse": {
      const t = weakestAlly(state, actor);
      if (t) heal(state, actor, t, t.maxHp * ((p["pct"] ?? 12) / 100) * m);
      return;
    }
    case "abl.repair_all": {
      for (const a of allies(state, actor)) heal(state, actor, a, a.maxHp * ((p["pct"] ?? 10) / 100) * m);
      return;
    }
    case "abl.overcharge": {
      const t = strongestFighter(state, actor);
      if (t) applyStatus(state, t, "atk_pct", (p["pct"] ?? 20) * m, p["ticks"] ?? 50, actor.input.id);
      return;
    }
    case "abl.haste":
    case "abl.pulse_overdrive": {
      for (const a of allies(state, actor)) applyStatus(state, a, "speed_pct", (p["pct"] ?? 20) * m, p["ticks"] ?? 50, actor.input.id);
      return;
    }
    case "abl.pulse_rally": {
      for (const a of allies(state, actor)) applyStatus(state, a, "atk_pct", (p["pct"] ?? 15) * m, p["ticks"] ?? 30, actor.input.id);
      return;
    }
    case "abl.energize":
    case "abl.pulse_inspire": {
      const amount = Math.round((p["amount"] ?? 25) * m);
      for (const a of allies(state, actor, false)) {
        a.energy = Math.min(ENERGY_MAX, a.energy + amount);
        state.events.push({ t: state.tick, k: "status", a: actor.input.id, b: a.input.id, v: amount, x: "energy" });
      }
      return;
    }
    case "abl.cleanse": {
      const count = Math.round(p["count"] ?? 2);
      for (const a of allies(state, actor)) {
        const negative = a.statuses.filter((s) => s.value < 0 || s.key === "marked" || s.key === "corrode" || s.key === "stun");
        for (const s of negative.slice(0, count)) a.statuses.splice(a.statuses.indexOf(s), 1);
        if (negative.length) state.events.push({ t: state.tick, k: "status", a: actor.input.id, b: a.input.id, v: Math.min(count, negative.length), x: "cleanse" });
      }
      return;
    }
    case "abl.reboot":
    case "abl.ultimate_reboot": {
      if (ab.usedOnce) return;
      const t = deadFighter(state, actor);
      if (!t) return;
      ab.usedOnce = true;
      t.alive = true;
      t.hp = Math.max(1, Math.round(t.maxHp * ((p["pct"] ?? 40) / 100) * m));
      t.energy = 0;
      t.nextAttackTick = state.tick + t.stats.intervalTicks;
      state.events.push({ t: state.tick, k: "revive", a: actor.input.id, b: t.input.id, v: t.hp });
      return;
    }
    default:
      throw new Error(`unhandled archetype ${ab.archetype}`);
  }
}

/** Corrode ticks every 10 ticks (one second): pctPerSec of the source's attack as sure damage. */
export function tickCorrode(state: BattleState, u: Unit): void {
  const c = status(u, "corrode");
  if (!c || state.tick % 10 !== 0) return;
  const source = state.sides[other(u.side)].units.find((s) => s.input.id === c.source) ?? state.sides[u.side].units.find((s) => s.input.id === c.source);
  if (!source) return;
  const dmg = Math.max(1, Math.round(source.stats.atk * (c.value / 100)));
  u.hp -= dmg;
  u.damageTaken += dmg;
  source.damageDealt += dmg;
  state.events.push({ t: state.tick, k: "damage", a: source.input.id, b: u.input.id, v: dmg, x: "corrode" });
  if (u.hp <= 0) onDeath(state, u, source.alive ? source : null);
}
